/** Botão de leitura em voz alta da página (US-03 / ADR-009).
 *
 * Sempre visível quando o navegador tem síntese de voz — não fica atrás do
 * interruptor de preferência. Esconder um recurso de acessibilidade atrás de
 * uma configuração obriga quem mais precisa dele a descobrir a configuração
 * primeiro, e o interruptor (`tts_enabled`) serve a outra coisa: ler sozinho ao
 * virar a página.
 *
 * O texto vem da camada JÁ RENDERIZADA, que só existe em página liberada —
 * então não há caminho por onde a voz leia capítulo bloqueado.
 */
import { useEffect } from 'react'

import { useTextToSpeech } from '@/features/reader/hooks/useTextToSpeech'
import { useAccessibilityStore } from '@/features/reader/stores/accessibilityStore'
import { textoDaCamada } from '@/features/reader/utils/tts'

interface ReadAloudButtonProps {
  /** A camada de texto desta página; `null` enquanto não renderizou. */
  camada: HTMLElement | null
  page: number
  /** `true` quando a camada terminou de montar — o texto só existe depois. */
  pronto: boolean
  deps?: Partial<Parameters<typeof useTextToSpeech>[0]>
}

export function ReadAloudButton({ camada, page, pronto, deps = {} }: ReadAloudButtonProps) {
  const velocidade = useAccessibilityStore((s) => s.preferencias.tts_rate)
  const automatico = useAccessibilityStore((s) => s.preferencias.tts_enabled)

  const { falar, parar, estado, disponivel } = useTextToSpeech({
    velocidade,
    contexto: page,
    ...deps,
  })

  // Leitura contínua: quem ligou o automático quer que virar a página já leia.
  // Depende de `pronto` porque antes disso a camada está vazia e a voz não
  // diria nada — o que pareceria um botão quebrado.
  useEffect(() => {
    if (!automatico || !pronto || !disponivel) return
    const texto = textoDaCamada(camada)
    if (texto !== '') falar(texto)
  }, [automatico, pronto, disponivel, camada, page, falar])

  if (!disponivel) return null

  const falando = estado === 'falando'
  return (
    <button
      type="button"
      className="pdf-tts"
      onClick={() => (falando ? parar() : falar(textoDaCamada(camada)))}
      disabled={!pronto}
      aria-label={falando ? 'Parar a leitura em voz alta' : 'Ouvir esta página em voz alta'}
    >
      {falando ? '⏹ Parar' : '🔊 Ouvir'}
    </button>
  )
}
